import { FC } from 'react'
import { tokenStorage } from '../../services/token-storage'
import { Navigate, useLocation } from 'react-router-dom'
import { routes } from '../../constants/routes'
import { PropsWithChildren } from 'react'
import { BusinessRole } from '../../types/api/user'
import { useHasRole } from '../../hooks/use-has-role'
import { getRoutePermissions } from '../../constants/route-permissions'

interface PrivateSectionProps {
    /**
     * Роли, которые требуются для доступа к этой секции
     * Если не указано, будет использована конфигурация из route-permissions
     * или требуется только аутентификация
     */
    requiredRoles?: BusinessRole | BusinessRole[]
    /**
     * Если true, пользователь должен иметь все указанные роли
     * Если false (по умолчанию), достаточно одной из указанных ролей
     * Если не указано, используется значение из конфигурации route-permissions
     */
    requireAll?: boolean
    /**
     * Куда перенаправить пользователя, если у него нет доступа
     * По умолчанию - на главную страницу
     */
    fallbackPath?: string
    /**
     * Путь роута для автоматической проверки прав из конфигурации
     * Если указан, requiredRoles и requireAll будут взяты из route-permissions
     */
    route?: string
}

const PrivateSection: FC<PropsWithChildren<PrivateSectionProps>> = ({
    children,
    requiredRoles: propsRequiredRoles,
    requireAll: propsRequireAll,
    fallbackPath = routes.home,
    route,
}) => {
    const location = useLocation()
    const auth = tokenStorage.get()

    // Если указан route, используем конфигурацию из route-permissions
    const routePermissions = route ? getRoutePermissions(route) : {}
    const requiredRoles = propsRequiredRoles ?? routePermissions.requiredRoles
    const requireAll = propsRequireAll ?? routePermissions.requireAll ?? false

    const hasRequiredRole = useHasRole(requiredRoles || [], requireAll)

    if (!auth) {
        // Redirect them to the /login page, but save the current location they were
        // trying to go to when they were redirected. This allows us to send them
        // along to that page after they login, which is a nicer user experience
        // than dropping them off on the home page.
        return <Navigate to={routes.login} state={{ from: location }} replace />
    }

    // Если указаны роли, проверяем их наличие
    if (requiredRoles && !hasRequiredRole) {
        return <Navigate to={fallbackPath} replace />
    }

    return <>{children}</>
}

export default PrivateSection
